import { useEffect, useState, useRef, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import type { AlchemyRecipe } from '../../../server/src/skills/AlchemySkillSystem';

export interface AlchemyState {
  level: number;
  xp: number;
  nextLevelXp: number;
  isBrewing: boolean;
  currentRecipe: AlchemyRecipe | null;
  itemsBrewed: number;
  batchSize: number;
  nextBrewTime: number | undefined;
}

export interface UseAlchemyReturn {
  alchemyState: AlchemyState;
  recipes: AlchemyRecipe[];
  startBrewing: (recipeId: string, quantity: number) => void;
  stopBrewing: () => void;
  isLoading: boolean;
  error: string | null;
  inventory: Record<string, number>; // itemId -> quantity
  canCraft: (recipe: AlchemyRecipe) => boolean;
}

/**
 * Hook to manage alchemy skill progression and active brewing sessions
 */
export const useAlchemy = (socketRef: React.MutableRefObject<Socket | null>): UseAlchemyReturn => {
  const [alchemyState, setAlchemyState] = useState<AlchemyState>({
    level: 1,
    xp: 0,
    nextLevelXp: 0,
    isBrewing: false,
    currentRecipe: null,
    itemsBrewed: 0,
    batchSize: 0,
    nextBrewTime: undefined,
  });

   const [recipes, setRecipes] = useState<AlchemyRecipe[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [inventory, setInventory] = useState<Record<string, number>>({});
   const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

   // Fetch available recipes and player alchemy stats
   const fetchAlchemyData = useCallback(() => {
     if (!socketRef.current || !socketRef.current.connected) {
       console.log('❌ Socket not connected, skipping recipe fetch');
       return;
     }

     console.log('🔄 Fetching alchemy data...');
     setIsLoading(true);
     
     // Set a timeout to stop loading if no response
     const timeout = setTimeout(() => {
       console.warn('⚠️ Alchemy data fetch timeout - no response from server');
       setIsLoading(false);
     }, 3000);
     
     // Request recipes
     socketRef.current.emit('alchemy:getRecipes', {});
     
     // Request stats
     socketRef.current.emit('alchemy:getStats', {});
     
     // Request inventory
     socketRef.current.emit('inventory:getData', {});

     return () => clearTimeout(timeout);
   }, [socketRef]);

  // Update brewing progress regularly
  const startProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('alchemy:getProgress', {});
    }, 500); // Update every 500ms
  }, [socketRef]);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Set up socket event listeners
  useEffect(() => {
    if (!socketRef.current) return;

    // Receive recipes
    const handleRecipes = (data: { recipes: AlchemyRecipe[]; playerLevel: number }) => {
      console.log(`🧪 Received alchemy recipes - Count: ${data.recipes?.length || 0}, Level: ${data.playerLevel}`);
      if (data.recipes && data.recipes.length > 0) {
        console.log(`   First recipe: ${data.recipes[0].name} (Level ${data.recipes[0].requiredLevel})`);
      }
      setRecipes(data.recipes);
      setIsLoading(false);
    };

    // Receive stats
    const handleStats = (data: {
      level: number;
      totalXp: number;
      currentXp: number;
      nextLevelXp: number;
      isBrewing: boolean;
      currentRecipe: AlchemyRecipe | null;
      itemsBrewed: number;
      batchSize: number;
      nextBrewTime: number | undefined;
    }) => {
      setAlchemyState({
        level: data.level,
        xp: data.currentXp,
        nextLevelXp: data.nextLevelXp,
        isBrewing: data.isBrewing,
        currentRecipe: data.currentRecipe,
        itemsBrewed: data.itemsBrewed,
        batchSize: data.batchSize,
        nextBrewTime: data.nextBrewTime,
      });
      setIsLoading(false);
    };

    // Receive progress updates
    const handleProgress = (data: {
      isBrewing: boolean;
      currentRecipe: AlchemyRecipe | null;
      itemsBrewed: number;
      batchSize: number;
      nextBrewTime: number | undefined;
      level?: number;
      currentXp?: number;
      nextLevelXp?: number;
    }) => {
      setAlchemyState(prev => ({
        ...prev,
        level: data.level ?? prev.level,
        xp: data.currentXp ?? prev.xp,
        nextLevelXp: data.nextLevelXp ?? prev.nextLevelXp,
        isBrewing: data.isBrewing,
        currentRecipe: data.currentRecipe,
        itemsBrewed: data.itemsBrewed,
        batchSize: data.batchSize,
        nextBrewTime: data.nextBrewTime,
      }));
    };

    // Handle brewing started
    const handleStarted = (data: { recipeId: string; quantity: number }) => {
      startProgressUpdates();
    };

    // Handle brewing stopped
    const handleStopped = (data: { brewedCount: number; failedCount: number }) => {
      stopProgressUpdates();
      setAlchemyState(prev => ({
        ...prev,
        isBrewing: false,
        currentRecipe: null,
        itemsBrewed: 0,
        batchSize: 0,
      }));
    };

     // Handle errors
     const handleError = (data: { message: string }) => {
       setError(data.message);
       stopProgressUpdates();
     };

     // Handle inventory update
     const handleInventory = (data: { items: Array<{ itemId: string; quantity: number }> }) => {
       const inv: Record<string, number> = {};
       data.items.forEach(item => {
         inv[item.itemId] = item.quantity;
       });
       setInventory(inv);
     };

      socketRef.current.on('alchemy:recipes', handleRecipes);
      socketRef.current.on('alchemy:stats', handleStats);
      socketRef.current.on('alchemy:progress', handleProgress);
      socketRef.current.on('alchemy:started', handleStarted);
      socketRef.current.on('alchemy:stopped', handleStopped);
      socketRef.current.on('alchemy:error', handleError);
      socketRef.current.on('inventory:data', handleInventory);

      const socket = socketRef.current;
      return () => {
        if (!socket) return;
        socket.off('alchemy:recipes', handleRecipes);
        socket.off('alchemy:stats', handleStats);
        socket.off('alchemy:progress', handleProgress);
        socket.off('alchemy:started', handleStarted);
        socket.off('alchemy:stopped', handleStopped);
        socket.off('alchemy:error', handleError);
        socket.off('inventory:data', handleInventory);
        stopProgressUpdates();
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startProgressUpdates, stopProgressUpdates]);

     // Fetch initial data on mount and whenever socket becomes available
     // eslint-disable-next-line react-hooks/exhaustive-deps
     useEffect(() => {
       const attemptFetch = () => {
         if (!socketRef.current?.connected) {
           // Socket not ready, try again soon
           console.log('⏳ Socket not connected, retrying in 100ms...');
           const timer = setTimeout(attemptFetch, 100);
           return () => clearTimeout(timer);
         }
         
         console.log('✅ Socket connected, fetching alchemy data');
         fetchAlchemyData();
       };

       return attemptFetch();
      }, [fetchAlchemyData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Start brewing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const startBrewing = useCallback(
      (recipeId: string, quantity: number) => {
        if (!socketRef.current) return;
        setError(null);
        socketRef.current.emit('alchemy:start', { recipeId, quantity });
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Stop brewing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stopBrewing = useCallback(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('alchemy:stop', {});
    }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

  // Check if player can craft a recipe
  const canCraft = useCallback((recipe: AlchemyRecipe): boolean => {
    // Check level requirement
    if (alchemyState.level < recipe.requiredLevel) return false;
    
    // Check all required items
    for (let i = 0; i < recipe.rawItemIds.length; i++) {
      const itemId = recipe.rawItemIds[i];
      const requiredQty = recipe.requiredQuantities[i];
      if ((inventory[itemId] || 0) < requiredQty) return false;
    }
    
    return true;
  }, [alchemyState.level, inventory]);

  return {
    alchemyState,
    recipes,
    startBrewing,
    stopBrewing,
    isLoading,
    error,
    inventory,
    canCraft,
  };
};
